import { useState, useEffect, useRef } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlus, faTrash, faEllipsisV, faFilePdf, faEye, faDownload } from "@fortawesome/free-solid-svg-icons"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { z } from "zod"
import useLocalStorageState from "use-local-storage-state"
import { useGetRequest, usePostRequest } from "../../../Utils/hooks/QueryHandler"
import { DeleteRequest, GetRequest } from "../../../Utils/hooks/ApiHandler"
import { toast } from "sonner"
import DeleteModal from "../../DeleteModal"
import pdfIcon from "../../../Utils/assets/img/icons/pdf_Icon.webp"
import { IconDownload } from "@tabler/icons-react"
import nodata from "../../../Utils/assets/img/leads_no_data_found.png"
import {
  IconXboxX
} from "@tabler/icons-react";
import { baseURL, imageURL } from "../../../Utils/constants/constant"
import { handleDownloadFile } from "../../../Utils/helpers/commonhelper"
import { UnauthViewModal } from "../../../CommonComponents/UnauthViewModal"
import { useOutsideClickHandler } from "../../../Utils/hooks/useOutsideClickHandler"
import { SubmitBtn } from "../../../CommonComponents/Buttons/SubmitBtn"
import { CancelBtn } from "../../../CommonComponents/Buttons/CancelBtn"
import { useGetEssentials } from "../../../Utils/hooks/useGetEssentials"

const documentSchema = z.object({
  document_name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  file: z
    .any()
    .refine((files) => files?.length > 0, "Please select a file")
    .refine((files) => {
      if (!files?.[0]) return false;
      const fileType = files[0].type;
      const allowedTypes = [
        'application/pdf',
        'image/png',
        'image/jpeg',
        'image/jpg'
      ];
      return allowedTypes.includes(fileType);
    }, "Only PDF, JPEG, PNG, JPG files are allowed")
    .refine((files) => files?.[0]?.size <= 5 * 1024 * 1024, "File size must be less than 5MB"),
});

export const Documents = ({ leadId, documentGrant, isActive = false, module_name, isLeadWon = false }) => {
  const { customer, setCustomer, headers, extras, navigate, customerId, loginUserId: userLoginId, userType } = useGetEssentials()
  const [documents, setDocuments] = useState([])
  const [showUploadForm, setShowUploadForm] = useState(false)
  const [showDocumentActions, setShowDocumentActions] = useState({})
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [documentToDelete, setDocumentToDelete] = useState(null)
  const [loading, setLoading] = useState(false)
  const [closing, setClosing] = useState(false)
  const dropdownRefs = useRef({})

  const { register, handleSubmit, reset, formState: { errors }, watch } =
    useForm({ resolver: zodResolver(documentSchema) })

  const watchedFile = watch("file")

  const [expandedTitles, setExpandedTitles] = useState({});

  // custom hook for outside click handler
  useOutsideClickHandler({
    objRef: dropdownRefs,
    isDropdown: showDocumentActions,
    setDropdown: setShowDocumentActions
  })

  const toggleTitle = (id) => {
    setExpandedTitles(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const truncate = (text, limit = 40) =>
    text.length > limit ? text.substring(0, limit) + "" : text;


  // Only fetch documents when tab is active
  const { data: documentData, refetch: fetchDocument } =
    useGetRequest(
      `lead/fetchDocuments/${customerId}/${leadId}/${module_name}/${userLoginId}`,
      "getDocument",
      headers,
      extras,
      { enabled: isActive && !!leadId && !!customerId }
    )

  const { mutate: addDocument } =
    usePostRequest(`lead/uploadDocument/`, true, { headers }, extras)

  useEffect(() => {
    if (documentData?.status === "success") {
      setDocuments(documentData?.data || [])
    }
  }, [documentData])

  // const handleDownloadFile = async (docPath, filename) => {
  //   try {
  //     const urlArr = docPath?.split("/")
  //     const docName = urlArr[urlArr.length - 1]
  //     const res = await GetRequest(`${baseURL}common/downloadfile/?file=${imageURL}${docPath}`, { headers }, extras)
  //     if (res.status === "fail" || res.success === false) {
  //       toast.error(res.message)
  //       return
  //     }
  //     const downloadUrl = res.data
  //     // Use direct link download to avoid CORS issue
  //     const link = window.document.createElement('a');
  //     link.href = downloadUrl;
  //     link.setAttribute('download', docName);
  //     window.document.body.appendChild(link);
  //     link.click();
  //     window.document.body.removeChild(link);
  //   } catch (error) {
  //     return
  //   }
  // }

  // const handleDownloadFile = async (document, filename) => {
  //   try {
  //     const urlArr = document?.split("/")
  //     const docName = urlArr[urlArr.length - 1]
  //     const res = await GetRequest(`${baseURL}common/downloadfile/?file=${imageURL}${document}`, { headers }, extras)
  //     if (res.status === "fail" || res.success === false) {
  //       toast.error(res.message)
  //       return
  //     }
  //     const downloadUrl = res.data
  //     const response = await fetch(downloadUrl);
  //     const blob = await response.blob();
  //     const link = document.createElement('a');
  //     link.href = URL.createObjectURL(blob);
  //     link.download = docName;
  //     document.body.appendChild(link);
  //     link.click();
  //     document.body.removeChild(link);
  //     URL.revokeObjectURL(link.href);
  //   } catch (error) {
  //     return
  //   }
  // }

  const onSubmit = async (formData) => {
    const file = formData.file?.[0]
    // if (!file || file.type !== "application/pdf, image/") return

    const payload = {
      lead_id: leadId,
      document: file,
      user_login_id: userLoginId,
      document_name: formData.document_name,
      description: formData.description,
      module_name: module_name
    }

    setLoading(true)
    addDocument(payload, {
      onSuccess: (res) => {
        if (res.status === "fail" || res.success === false) {
          toast.error(res.message)
          return
        }
        toast.success(res.msg || "Document added successfully")
        fetchDocument()
        reset()
        setShowUploadForm(false)
      },
      onError: (err) => {
        toast.error(err?.msg || "Add document request failed")
      },
      onSettled: () => setLoading(false)
    })
  }

  const toggleDocumentActions = (documentId) => {
    setShowDocumentActions(prev => ({
      ...Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: false }), {}),
      [documentId]: !prev[documentId]
    }))
  }

  const handleViewDocument = (document) => {
    if (document?.document) {
      window.open(`${imageURL}${document.document}`, "_blank")
    } else {
      toast.error("No file URL found")
    }
    setShowDocumentActions({})
  }

  const handleDeleteDocument = (document) => {
    setDocumentToDelete(document)
    setShowDeleteModal(true)
    setShowDocumentActions({})
  }

  const confirmDeleteDocument = async () => {
    if (!documentToDelete) return
    setLoading(true)
    try {
      const response = await DeleteRequest(
        `lead/DeleteDocuments/${leadId}/${documentToDelete.id}`,
        { headers },
        extras
      )

      if (response?.status === "success") {
        toast.success(response.msg || "Document deleted successfully")
        setLoading(false)
        fetchDocument()
      } else {
        toast.error(response?.msg || "Failed to delete document")
        setLoading(false)
      }
    } catch (error) {
      toast.error("Error deleting document")
      setLoading(false)
    } finally {
      setLoading(false)
      setShowDeleteModal(false)
      setDocumentToDelete(null)
    }
  }

  const handleCloseDeleteModal = () => {
    setClosing(true)
    setTimeout(() => {
      setShowDeleteModal(false)
      setDocumentToDelete(null)
      setClosing(false)
    }, 250)
  }

  return (
    // <div className="document-container" style={{ minHeight: "calc(100vh - 10.5rem)", maxHeight: "calc(100vh - 10.5rem)", overflowY: "auto", overflowX: "hidden", position: 'relative' }}>
    <>
      <div className="d-flex justify-content-between align-items-center mb-0 px-3">
        <div
          className=" mb-0"
          style={{ fontSize: "19px", fontWeight: "300" }}
        >
          Documents
        </div>
        <button
          className="btn btn-default-outline"
          onClick={() => {
            if (!documentGrant.isCreate) {
              toast.warning("You are not authorized for this action!")
              return
            }
            setShowUploadForm(!showUploadForm)
          }}
          style={{
            fontSize: "13px",
            // borderBottom: '1px solid #e3e3e3',
            height: "30px",
            width: "auto",
            gap: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "2px",
            color: "#fff",
          }}
          disabled={isLeadWon}
        >
          <FontAwesomeIcon icon={faPlus} /> Add
        </button>
      </div>

      <hr />

      {showUploadForm && (
        <div className="upload-form">
          <h4>Upload Document</h4>
          <form
            onSubmit={handleSubmit(onSubmit)}
            encType="multipart/form-data"
            onKeyDown={(e) => {
              const isTextarea = e.target.tagName === "TEXTAREA"
              const isFileInput = e.target.type === "file"
              if (e.key === "Enter") {
                e.preventDefault()
                handleSubmit(onSubmit)()
              }
            }}
          >
            <div className="form-group">
              <input
                type="text"
                className={`form-control ${errors.document_name ? 'is-invalid' : ''}`}
                placeholder="Document Name"
                {...register("document_name")}
              />
              {errors.document_name && (
                <div className="invalid-feedback">{errors.document_name.message}</div>
              )}
            </div>

            <div className="form-group">
              <textarea
                className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                placeholder="Document Description"
                rows="3"
                {...register("description")}
              />
              {errors.description && (
                <div className="invalid-feedback">{errors.description.message}</div>
              )}
            </div>

            <div className="form-group">
              <input
                type="file"
                className={`form-control ${errors.file ? 'is-invalid' : ''}`}
                accept=".pdf,application/pdf,.jpeg, application/jpeg, .png, application/png"
                {...register("file")}
              />
              {errors.file && (
                <div className="invalid-feedback">{errors.file.message}</div>
              )}
              {watchedFile?.[0] && (
                <small className="file-info align-items-center">
                  <>{`Selected: ${watchedFile[0].name} ${(watchedFile[0].size / 1024 / 1024).toFixed(2)} MB `}  <IconXboxX
                    stroke={2}
                    size={18}
                    style={{ stroke: "red", cursor: "pointer" }}
                    onClick={() => reset({ file: null })}
                  /></>
                </small>

              )}
              <p
                style={{
                  color: "#a5a5a5",
                  fontSize: "11px",
                  fontWeight: 400,
                }}
              >
                Accepted file types are .jpg, .png, .pdf, .jpeg,  max file size
                5MB
              </p>
            </div>

            <div className="form-actions">
              <CancelBtn
                type={'button'}
                onClick={() => {
                  setShowUploadForm(false)
                  reset()
                }}
                submitClicked={loading}
              />
              <SubmitBtn
                type={'submit'}
                loading={loading}
                title="Upload"
              />
            </div>
          </form>
        </div>
      )}

      <div className="listStyleForContact" style={{ maxHeight: "calc(100vh - 12rem)", minHeight: "calc(100vh - 12rem)", overflowX: "auto", scrollBehavior: "smooth", scrollbarWidth: "thin" }}
        onMouseEnter={(e) => {
          e.currentTarget.style.scrollbarColor = "#e0e0e0 white"
        }}>
        {
          documentGrant?.isView ?
            documents.length === 0 ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ position: "absolute", left: "24%" }}
              >
                <img
                  src={nodata}
                  alt=""
                  style={{ width: "17rem", marginTop: "0" }}
                />
              </div>
            ) : (
              documents.map((document) => (
                <div
                  key={document.id}
                  className="d-flex align-items-start gap-2 justify-content-between p-3"
                  style={{ borderBottom: "1px solid lightblue", borderLeft: "none", borderRight: "none", }}
                >
                  <div
                    // onClick={() => { handleOpenFile(`${baseURL}/${document.document}`) }}
                    style={{ cursor: "pointer" }}
                    className="d-flex align-items-center me-2"
                  >
                    <img src={pdfIcon} alt="" style={{ width: 50 }} />
                  </div>

                  <div className="document-details flex-grow-1">
                    <h5 style={{ textTransform: "capitalize" }}>{document.document_name}</h5>
                    <p className="meeting-title d-flex flex-column align-items-start">
                      <div className="title-row">
                        <span className="title-text">
                          {expandedTitles[document.id]
                            ? document.description
                            : truncate(document.description, 40)}
                        </span>

                        {document.description?.length > 40 && !expandedTitles[document.id] && (
                          <button
                            className="toggle-btn more-btn"
                            onClick={() => toggleTitle(document.id)}
                            aria-label="Show more"
                          >
                            ...
                          </button>
                        )}
                      </div>

                      {expandedTitles[document.id] && (
                        <div className="toggle-bottom">
                          <button
                            className="toggle-btn less-btn"
                            onClick={() => toggleTitle(document.id)}
                            aria-label="Show less"
                          >
                            Show Less
                          </button>
                        </div>
                      )}
                    </p>
                    <small>Uploaded on {new Date(document.created_at).toLocaleDateString()}</small>
                  </div>

                  {/* Download button */}
                  <div className="d-flex align-self-start ">
                    <button
                      className="btn bg-white pt-0 pb-3 d-flex justify-content-center align-items-center me-2"
                      style={{
                        width: "2.6rem",
                        height: "2.6rem",
                        fontSize: 12,
                      }}
                      onClick={() => handleDownloadFile({ docPath: document?.document, headers, extras })}
                    >
                      <IconDownload stroke={2} />
                    </button>
                  </div>

                  <div className="document-actions d-flex align-self-start ">
                    <div className="dropdown-wrapper" ref={el => dropdownRefs.current[document.id] = el}>
                      {
                        !isLeadWon &&
                        <button
                          variant="none"
                          style={{ border: 'none', background: 'none' }}
                          className="dropdownTogglesBtns"
                          onClick={(e) => {
                            e.stopPropagation()
                            toggleDocumentActions(document.id)
                          }}
                        >
                          <FontAwesomeIcon icon={faEllipsisV} />
                        </button>
                      }
                      {showDocumentActions[document.id] && (
                        <div className="dropdown-menu show">
                          <button
                            className="dropdown-item"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleViewDocument(document)
                            }}
                          >
                            <FontAwesomeIcon icon={faEye} /> View
                          </button>
                          <button
                            className="dropdown-item text-danger"
                            onClick={(e) => {
                              if (!documentGrant.isDelete) {
                                toast.warning("You are not authorized for this action!")
                                return
                              }
                              e.stopPropagation()
                              handleDeleteDocument(document)
                            }}
                          >
                            <FontAwesomeIcon icon={faTrash} /> Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )
            :
            <UnauthViewModal tabName={"Documents"} />
        }

        <DeleteModal
          show={showDeleteModal}
          onClose={handleCloseDeleteModal}
          onConfirm={confirmDeleteDocument}
          isLoading={loading}
          message="Are you sure you want to delete this Document?"
        />
      </div>


      <style jsx>{`
        .document-container {
          // padding: 20px;
          height : calc(100vh - 4rem);
          }
        
        .document-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 15px;
        }
        
        .upload-form {
          background: #fff;
          border: 1px solid #e0e4f8;
          border-radius: 8px;
          padding: 20px;
          margin-bottom: 20px;
        }
        
        .form-group {
          margin-bottom: 15px;
        }
        
        .form-actions {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          margin-top: 20px;
        }
        
        .file-info {
          display: block;
          margin-top: 5px;
          color: #6c757d;
        }
        
        .document-list {
          max-height: calc(100vh - 250px);
        }
        
        .empty-state {
          text-align: center;
          padding: 40px 0;
          color: #6c757d;
        }
        
        .document-card {
          display: flex;
          align-items: center;
          padding: 15px;
          margin-bottom: 15px;
          background: #fff;
          border: 1px solid #e0e4f8;
          border-radius: 8px;
          position: relative;
        }
        
        .document-icon {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f8f9fa;
          border-radius: 8px;
          margin-right: 15px;
          font-size: 20px;
        }
        
        .document-details {
          flex: 1;
        }
        
        .document-details h5 {
          font-size: 14px;
          font-weight: 500;
          margin-bottom: 5px;
        }
        
        .document-details p {
          font-size: 12px;
          color: #6c757d;
          margin-bottom: 5px;
        }
        
        .document-details small {
          font-size: 11px;
          color: #6c757d;
        }
        
        .document-actions {
          position: relative;
        }
        
        .dropdown-wrapper {
          position: relative;
          display: inline-block;
        }
        
        .dropdown-menu {
          position: absolute;
          right: 0;
          top: 100%;
          min-width: 150px;
          background: #fff;
          border: 1px solid rgba(0,0,0,.15);
          border-radius: 4px;
          box-shadow: 0 2px 4px rgba(0,0,0,.1);
          z-index: 1000;
          padding: 5px 0;
          display: none;
        }
        
        .dropdown-menu.show {
          display: block;
        }
        
        .dropdown-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 5px 15px;
          width: 100%;
          text-align: left;
          background: none;
          border: none;
          color: #212529;
          cursor: pointer;
          font-size: 14px;
        }
        
        .dropdown-item:hover {
          background-color: #f8f9fa;
        }
        
        .dropdown-item.text-danger {
          color: #dc3545;
        }
      `}</style>
    </ >
  )
}